<template>
  <BasePageContainer class="page follow member-setting pb-15">
    <div class="page__head follow-head fixed-top p-3">
      <h1 class="follow-head__heading">
        <router-link
          to="/settings/"
          class="follow-head__back">
          <span class="material-icons">
            arrow_back_ios
          </span>
        </router-link>
        フォローの追加・編集
      </h1>
    </div>
    <div class="page__body follow-content pt-8"> 
      <p>
        チェックを入れたグループorメンバーの新規活動が通知画面に表示されます
      </p>
    </div>

    <SearchUserForm
      @submit="searchAndScrollToMember" />

    <ul
      class="list-unstyled mt-3">
      <follow-department-list-item-menu
        v-for="department in sortedAccessibleDepartments"
        :id="department.id"
        :key="department.id"
        :ref="department.id"
        :department="department"
        :show-members="isShowedDepartment(department.id)"
        :is-checked-department="isCheckedDepartment(department.id)" 
        class="mt-3"
        @department-checked="onDepartmentChecked"
        @click.native="onClickDepartmentListItemMenu(department.id)">
        <template #pin-icon>
          <div
            class="pin-icon-button"
            @click.stop="togglePin(department.id)">
            <pin-icon :is-pinned="isPinned(department.id)" />
          </div>
        </template>
        <follow-department-member-list-item
          v-for="member in membersByDepartmentId[department.id]"
          :ref="member.id"
          :key="member.id"
          :member="member"
          :is-checked-department="isCheckedDepartment(department.id)" 
          :is-checked-member="isCheckedMember(member)"
          :is-searched="isSearched(member)"
          @member-checked="onMemberChecked(member)" />
      </follow-department-list-item-menu>
    </ul> 

    <div class="general-form__btn row mt-5">
      <div class="col-6">
        <button
          type="button"
          class="btn btn-block btn-cancel"
          @click="onClickCancel">
          キャンセル
        </button>
      </div>
      <div class="col-6">
        <button
          type="button"
          class="btn btn-block btn-primary"
          @click="onClickSave">
          保存
        </button>
      </div>
    </div>
    <ResultListScroller
      v-if="searchedMembers.length"
      :counts="searchedMembers.length"
      @onClickPrev="scrollToPrev"
      @onClickNext="scrollToNext"
      @onClickClose="resetSearchResult" />
  </BasePageContainer>
</template>

<script>
import FollowDepartmentListItemMenu from '@/components/view/FollowDepartmentListItemMenu.vue'
import FollowDepartmentMemberListItem from '@/components/view/FollowDepartmentMemberListItem.vue'
import BasePageContainer from '@/components/parts/BasePageContainer.vue'
import SearchUserForm from '@/components/view/SearchUserForm.vue'
import ResultListScroller from '@/components/parts/ResultListScroller.vue'
import { mapGetters, mapState } from 'vuex'
import { uniq } from 'lodash'
import PinIcon from '@/components/parts/PinIcon.vue'

export default {
  components: {
    BasePageContainer,
    FollowDepartmentListItemMenu,
    FollowDepartmentMemberListItem,
    SearchUserForm,
    PinIcon,
    ResultListScroller
  },
  data() {
    return {
      checkedDepartments: [],
      checkedMembers: [],
      follows: [],
      searchKeyword: '',
      membersByDepartmentId: {},
      showedDepartmentIds: [],
      searchedMembers: [],
      currentSearchedMembersIndex: 0
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['accessibleDepartments']),
    ...mapState('departments', ['pinnedDepartments']),
    _pinnedDepartments: {
      get() {
        return this.pinnedDepartments
      },
      set(v) {
        this.$store.commit('departments/setPinnedDepartments', v)
      }
    },
    accessibleDepartmentIds() {
      return this.accessibleDepartments.map(department => department.id)
    },
    sortedAccessibleDepartments() {
      const [pinnedDepartments, restDepartments] = this.accessibleDepartments.reduce((prev, curr) => {
        if (this.pinnedDepartments.includes(curr.id)) {
          prev[0].push(curr)
        } else {
          prev[1].push(curr)
        }
        return prev
      }, [[], []])
      return [...pinnedDepartments, ...restDepartments]
    },
    // $elがあるため、レンダリング終了まで計算が終わらない -> $nextTickでラップする必要がある
    sortedSearchedMembers() {
      return [...this.searchedMembers].sort((a, b) => {
        const aTop = this.$refs[a.id][0].$el.getBoundingClientRect().top
        const bTop = this.$refs[b.id][0].$el.getBoundingClientRect().top
        if (aTop <= bTop) {
          return -1
        } else {
          return 1
        }
      })
    },
    searchedMembersDepartmentIds() {
      return uniq(this.searchedMembers.map(member => member.department.id))
    }
  },
  async created() {
    this.$store.commit('startLoading')
    await Promise.all([
      this.$store.dispatch('auth/updateUser'),
      this.fetchFollows()
    ])
    this.$store.commit('finishLoading')
  },
  methods: {
    isShowedDepartment(departmentId) {
      return this.showedDepartmentIds.includes(departmentId)
    },
    async searchAndScrollToMember(searchKeyword) {
      // メンバーを検索する
      const res = await this.$axios.get('/users/', {
        params: {
          search: searchKeyword,
          ordering: '-sort_no',
          filter_accessible_departments: true
        }
      }).catch(error => {
        console.error(error)
        this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合はログインページ下のアプリ更新を押してください。'})
      })

      this.searchedMembers = res.data.results
      const searchedMembersDepartmentIds = uniq(this.searchedMembers.map(member => member.department.id))
      
      if (searchedMembersDepartmentIds.length === 0) {
        this.$store.commit('notify/set', {message: 'メンバーが見つかりませんでした'})
        return
      }

      await Promise.all(
        searchedMembersDepartmentIds.map(departmentId => this.fetchMembersByDepartmentId(departmentId))
      )
      // メンバーが所属するグループを全て展開
      this.showedDepartmentIds = Array.from(new Set([...this.showedDepartmentIds, ...searchedMembersDepartmentIds]))
      
      // 検索されたメンバーのうち、画面上での最初のメンバーの要素にスクロール
      this.$nextTick(() => {
        const topMember = this.searchedMembers.reduce((prev, curr) => {
          const currentTop = this.$refs[curr.id][0].$el.getBoundingClientRect().top
          const prevTop = this.$refs[prev.id][0].$el.getBoundingClientRect().top
          return currentTop < prevTop ? curr : prev
        }, this.searchedMembers[0])
        this.scrollToMember(topMember)
      })
    },
    scrollToMember(member) {
      const memberEl = this.$refs[member.id][0].$el
      const targetTop = memberEl.getBoundingClientRect().top + window.pageYOffset - 60
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    setCheckedDepartments() {
      const arr = this.follows.filter(f => !!f.department)
      this.checkedDepartments = arr.map(a => a.department.id)
    },
    setCheckedMembers() {
      const arr = this.follows.filter(f => !!f.user)
      this.checkedMembers = arr.map(a => a.user)
    },
    isCheckedDepartment(departmentId) {
      return !!this.checkedDepartments.find(c => c === departmentId)
    },
    onDepartmentChecked(departmentId) {
      if (this.checkedDepartments.find(c => c === departmentId)) {
        this.checkedDepartments = this.checkedDepartments.filter(c => c !== departmentId)
        // チェックがついた場合
      } else {
        this.checkedDepartments.push(departmentId)
        // 個別にチェック済みのメンバーの中に、チェックされたグループの所属メンバーがいたら除外する
        this.checkedMembers = this.checkedMembers.filter(c => {
          /* 
            APIの'/follow/'から取得したメンバー情報の中のdepartmentは文字列のidであり、
           '/users/'から取得した場合はdepartmentはオブジェクトで、その中にdepartmentのidがある
           という構造となっていて、それぞれ構造が若干異なるため以下のような分岐が必要。 
           */
          if (typeof c.department === 'string') {
            
            return c.department !== departmentId
          } else {
            return c.department.id !== departmentId
          }
        })
      }
    },
    onMemberChecked(member) {
      if (this.checkedMembers.find(c => c.id === member.id)) {
        this.checkedMembers = this.checkedMembers.filter(c => c.id !== member.id)
      } else {
        this.checkedMembers.push(member)
      }
    },
    onClickCancel() {
      this.$router.push('/settings')
    },
    async fetchFollows() {
      const follows = await this.$axios.get('/follow/')
      this.follows = follows.data.results
      this.setCheckedDepartments()
      this.setCheckedMembers()
    },
    // フォローしているグループorユーザー全てフォロー解除する
    resetFollows() {
      const resetMethods = this.follows.map((f) => {
        return this.$axios.delete(`/follow/${f.id}/`)
      })
      return Promise.all(resetMethods)
    },
    followDepartments() {
      const departmentMethods = this.checkedDepartments.map(departmentId => {
        const postBody = {
          department: departmentId
        }
        return this.$axios.post('/follow/', postBody)
      })
      return Promise.all(departmentMethods)
    },
    followUsers() {
      const memberMethods = this.checkedMembers.map(member => {
        const postBody = {
          user: member.id
        }
        return this.$axios.post('/follow/', postBody)
      })
      return Promise.all(memberMethods)
    },
    async onClickSave() {
      this.$store.commit('startLoading')
      try {
        // すでに何かをフォローしている場合はそのフォローをリセット 
        if (this.follows.length > 0) {
          await this.resetFollows()
        }
        await this.followDepartments()
        await this.followUsers()

        this.$router.push('/settings')
        this.$store.commit('notify/set', {message: '保存しました。'})
      } catch {
        this.$store.commit('notify/set', {type: 'danger', message: '何らかのエラーにより、フォローの設定ができませんでした。しばらく時間をおいてアクセスし直してください。'})
      }
      this.$store.commit('finishLoading')
    },
    fetchMembersByDepartmentId(departmentId) {
      return this.$axios.get('/users/', {
        params: {
          ordering: '-sort_no',
          limit: 99999,
          department__in: departmentId,
        }
      }).then(res => {
        this.$set(this.membersByDepartmentId, departmentId, res.data.results)
      })
    },
    isCheckedMember(member) {
      return !!this.checkedMembers.find(c => c.id === member.id)
    },
    onClickDepartmentListItemMenu(departmentId) {
      if (this.showedDepartmentIds.includes(departmentId)) {
        this.showedDepartmentIds = this.showedDepartmentIds.filter(showedDepartmentId => showedDepartmentId !== departmentId)
      } else {
        this.fetchMembersByDepartmentId(departmentId)
        this.showedDepartmentIds.push(departmentId)
      }
    },
    togglePin(departmentId) {
      if (this.isPinned(departmentId)) {
        this._pinnedDepartments = this._pinnedDepartments.filter(pinnedId => pinnedId !== departmentId)
      } else {
        this._pinnedDepartments = [...this._pinnedDepartments, departmentId]
      }
    },
    isPinned(departmentId) {
      return this.$store.getters['departments/isPinned'](departmentId)
    },
    isSearched(member) {
      return this.searchedMembers.map(member => member.id).includes(member.id)
    },
    scrollToPrev() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = this.currentSearchedMembersIndex === 0 ? this.searchedMembers.length - 1 : this.currentSearchedMembersIndex - 1
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    scrollToNext() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = (this.currentSearchedMembersIndex + 1) % this.searchedMembers.length
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    resetSearchResult() {
      this.searchedMembers = []
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/stylesheets/members';
.follow{
  padding-bottom: calc(200px + env(safe-area-inset-bottom)) !important;
  &__add{
    right: 16px;
    position: fixed;
    bottom: 0px;
    padding-bottom: calc(80px + constant(safe-area-inset-bottom));
    padding-bottom: calc(80px + env(safe-area-inset-bottom));
  }
  &__add-btn{
    box-shadow: 1px 2px 6px $grey05;
  }
  &__add-icon{//ボタン
    font-size: 30px;
    line-height: 54px;
    width: 30px;
  }
}

.follow-head{
  background-color: $l-blue;
  @media screen and (min-width:992px) {
    right: 0;
    left: 120px;
  }
  &__back{
    height: 22px;
    width: auto;
    color: $white;
    display: inline-block;
    vertical-align: text-bottom;
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
  }
}

.follow-group{
  &__icon{
    vertical-align: middle;
    display: inline-block;
  }
  &__item{
    position: relative;
    background-color: $white;
    border-radius: 8px;
    font-weight: bold;
  }
  
  &__btn{
    position: relative;
    box-shadow: 1px 2px 6px $grey05;
    padding: 2px;
    line-height: 27px;
    @media screen and (min-width:768px) {
       padding: auto;
       line-height: auto;
    }

  }
  &__btn-icon{
    display: inline-block;
    vertical-align: text-bottom;
    font-size: 20px;
  }
}

.pin-icon-button {
  margin: -15px -8px 0 15px;

  .pin-icon {
    width: 13px;
  }
}

.result-list-scroller {
  bottom: calc(env(safe-area-inset-bottom) * 2 + 70px);
  right: 22px;
}

</style>